import React, { useEffect, useState } from "react";
import { Select, Modal, message } from "antd";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertFromRaw, convertToRaw, ContentState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { getClinicUsers } from "../../../../apis/user";
import { getProcedures } from "../../../../apis/procedures";
import {
  OtTemplate,
  getOtTemplates,
  createOtTemplate,
  updateOtTemplate,
  deleteOtTemplate,
} from "../../../../apis/otTemplates";

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const MINUTE_OPTIONS = ["00", "15", "30", "45"];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));

interface ClinicUser {
  id: number;
  firstName?: string;
  first_name?: string;
  lastName?: string;
  last_name?: string;
  roles?: string[];
}

interface ProcedureRow {
  id: number | null;
  name: string;
}

interface OTFormProps {
  initialData?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const userName = (u: ClinicUser) =>
  `${u.firstName ?? u.first_name ?? ""} ${u.lastName ?? u.last_name ?? ""}`.trim() ||
  `User ${u.id}`;

const parseProcedures = (initialData?: any): ProcedureRow[] => {
  if (Array.isArray(initialData?.procedures) && initialData.procedures.length > 0) {
    return initialData.procedures.map((p: any) =>
      typeof p === "string"
        ? { id: null, name: p }
        : { id: p.id ?? null, name: p.name ?? "" }
    );
  }
  if (initialData?.procedureName) {
    return [{ id: null, name: initialData.procedureName }];
  }
  return [{ id: null, name: "" }];
};

const parseTime = (time?: string): { h: string; m: string } => {
  if (!time) return { h: "", m: "00" };
  const [h = "", rawM = "00"] = time.split(":");
  const mNum = parseInt(rawM, 10) || 0;
  const snapped = [0, 15, 30, 45].reduce((best, v) =>
    Math.abs(v - mNum) < Math.abs(best - mNum) ? v : best
  , 0);
  return { h, m: String(snapped).padStart(2, "0") };
};

// Extract {{VAR_NAME}} patterns from text
const extractVars = (text: string): string[] => {
  const matches = text.match(/\{\{([A-Z0-9_]+)\}\}/g) ?? [];
  const unique: string[] = [];
  matches.forEach((m) => {
    if (!unique.includes(m)) unique.push(m);
  });
  return unique;
};

const applyVars = (text: string, vars: Record<string, string>): string => {
  let result = text;
  Object.entries(vars).forEach(([placeholder, value]) => {
    result = result.replaceAll(placeholder, value || placeholder);
  });
  return result;
};

// ── Template manager modal ──────────────────────────────────────────────────

interface TemplateManagerProps {
  onClose: () => void;
  onTemplatesChanged: () => void;
}

const BLANK_TEMPLATE: Omit<OtTemplate, "id"> = {
  name: "",
  operationDetails: "",
  findings: "",
  complications: "",
  implants: "",
  notes: "",
};

const TemplateManager: React.FC<TemplateManagerProps> = ({ onClose, onTemplatesChanged }) => {
  const [templates, setTemplates] = useState<OtTemplate[]>([]);
  const [editing, setEditing] = useState<OtTemplate | null>(null);
  const [draft, setDraft] = useState<Omit<OtTemplate, "id">>(BLANK_TEMPLATE);
  const [saving, setSaving] = useState(false);

  const load = () => {
    getOtTemplates().then((res: any) => {
      const raw = res?.data ?? res;
      setTemplates(Array.isArray(raw) ? raw : raw?.data ?? []);
    });
  };

  useEffect(() => { load(); }, []);

  const handleEdit = (t: OtTemplate) => {
    setEditing(t);
    setDraft({
      name: t.name,
      operationDetails: t.operationDetails ?? "",
      findings: t.findings ?? "",
      complications: t.complications ?? "",
      implants: t.implants ?? "",
      notes: t.notes ?? "",
    });
  };

  const handleNew = () => {
    setEditing({ id: -1, name: "" });
    setDraft({ ...BLANK_TEMPLATE });
  };

  const handleSave = async () => {
    if (!draft.name.trim()) { message.error("Template name is required"); return; }
    setSaving(true);
    try {
      if (editing!.id === -1) {
        await createOtTemplate(draft);
      } else {
        await updateOtTemplate(editing!.id, draft);
      }
      message.success("Template saved");
      setEditing(null);
      load();
      onTemplatesChanged();
    } catch {
      message.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    await deleteOtTemplate(id);
    message.success("Template deleted");
    load();
    onTemplatesChanged();
  };

  const fieldCls =
    "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary";

  return (
    <div className="p-4 space-y-4">
      {!editing ? (
        <>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {templates.length === 0 ? "No templates yet." : `${templates.length} template(s)`}
            </span>
            <button
              onClick={handleNew}
              className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90"
            >
              + New Template
            </button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-gray-800">{t.name}</div>
                  {t.findings && (
                    <div className="text-xs text-gray-400 mt-0.5 line-clamp-1">
                      {t.findings.substring(0, 80)}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0 ml-3">
                  <button
                    onClick={() => handleEdit(t)}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(null)} className="text-sm text-gray-500 hover:text-gray-800">
              ← Back
            </button>
            <span className="text-sm font-medium text-gray-700">
              {editing.id === -1 ? "New Template" : `Edit: ${editing.name}`}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Template Name *</label>
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Appendectomy, LSCS, Hernia Repair"
              className={fieldCls}
            />
          </div>

          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
            Use <code className="font-mono bg-amber-100 px-1 rounded">{"{{VARIABLE_NAME}}"}</code> for placeholders.
            E.g. <code className="font-mono bg-amber-100 px-1 rounded">{"{{FINDING}}"}</code>,{" "}
            <code className="font-mono bg-amber-100 px-1 rounded">{"{{BLOOD_LOSS}}"}</code>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Operation Details</label>
            <textarea
              value={draft.operationDetails ?? ""}
              onChange={(e) => setDraft({ ...draft, operationDetails: e.target.value })}
              rows={4}
              placeholder="Under GA/SA, patient placed in... Incision made at... {{FINDING}}"
              className={fieldCls}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Findings</label>
              <textarea
                value={draft.findings ?? ""}
                onChange={(e) => setDraft({ ...draft, findings: e.target.value })}
                rows={3}
                className={fieldCls}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Complications</label>
              <textarea
                value={draft.complications ?? ""}
                onChange={(e) => setDraft({ ...draft, complications: e.target.value })}
                rows={3}
                className={fieldCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Implants</label>
              <textarea
                value={draft.implants ?? ""}
                onChange={(e) => setDraft({ ...draft, implants: e.target.value })}
                rows={2}
                className={fieldCls}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                value={draft.notes ?? ""}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                rows={2}
                className={fieldCls}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Template"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main OTForm ─────────────────────────────────────────────────────────────

const OTForm: React.FC<OTFormProps> = ({ initialData, onSave, onCancel }) => {
  const [doctors, setDoctors] = useState<ClinicUser[]>([]);
  const [procedureMaster, setProcedureMaster] = useState<{ value: number; label: string }[]>([]);
  const [procedures, setProcedures] = useState<ProcedureRow[]>(parseProcedures(initialData));
  const [timeH, setTimeH] = useState(parseTime(initialData?.procedureTime).h);
  const [timeM, setTimeM] = useState(parseTime(initialData?.procedureTime).m);
  const [formData, setFormData] = useState({
    procedureDate: initialData?.procedureDate || "",
    status: initialData?.status || "scheduled",
    surgeon1: (initialData?.surgeon1 ?? null) as number | null,
    surgeon2: (initialData?.surgeon2 ?? null) as number | null,
    anaesthetists: (initialData?.anaesthetists ?? null) as number | null,
    assistants: (initialData?.assistants ?? null) as number | null,
    findings: initialData?.findings || "",
    bloodLossMl: initialData?.bloodLossMl || "",
    complications: initialData?.complications || "",
    implants: initialData?.implants || "",
    notes: initialData?.notes || "",
  });
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

  // Template state
  const [templates, setTemplates] = useState<OtTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templateVars, setTemplateVars] = useState<Record<string, string>>({});
  const [activeTemplate, setActiveTemplate] = useState<OtTemplate | null>(null);
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  const loadTemplates = () => {
    getOtTemplates().then((res: any) => {
      const raw = res?.data ?? res;
      setTemplates(Array.isArray(raw) ? raw : raw?.data ?? []);
    });
  };

  useEffect(() => {
    getClinicUsers().then((res: any) => {
      const raw = res?.data ?? res;
      const data: ClinicUser[] = Array.isArray(raw) ? raw : raw?.data ?? [];
      setDoctors(data.filter((u) => Array.isArray(u.roles) && u.roles.includes("doctor")));
    });

    getProcedures({ per_page: 500, active_only: true }).then((res: any) => {
      const raw = res?.data ?? res;
      const list: any[] = Array.isArray(raw) ? raw : raw?.data ?? [];
      setProcedureMaster(list.map((p) => ({ value: p.id, label: p.name })));
    });

    loadTemplates();
  }, []);

  useEffect(() => {
    const raw = initialData?.operationDetails;
    if (raw) {
      try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        setEditorState(EditorState.createWithContent(convertFromRaw(parsed)));
      } catch {
        setEditorState(
          EditorState.createWithContent(ContentState.createFromText(String(raw)))
        );
      }
    } else {
      setEditorState(EditorState.createEmpty());
    }
  }, [initialData?.operationDetails]);

  // When vars change, re-apply them to the active template
  const applyTemplateWithVars = (template: OtTemplate, vars: Record<string, string>) => {
    const substitute = (text?: string) => applyVars(text ?? "", vars);

    const opDetails = substitute(template.operationDetails);
    setEditorState(
      EditorState.createWithContent(ContentState.createFromText(opDetails))
    );
    setFormData((prev) => ({
      ...prev,
      findings: substitute(template.findings),
      complications: substitute(template.complications),
      implants: substitute(template.implants),
      notes: substitute(template.notes),
    }));
  };

  const handleTemplateSelect = (templateId: number | undefined) => {
    if (!templateId) {
      setSelectedTemplateId(null);
      setActiveTemplate(null);
      setTemplateVars({});
      return;
    }
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    setSelectedTemplateId(templateId);
    setActiveTemplate(template);

    // Extract all unique vars from all fields
    const allText = [
      template.operationDetails,
      template.findings,
      template.complications,
      template.implants,
      template.notes,
    ]
      .filter(Boolean)
      .join("\n");

    const vars = extractVars(allText);
    const initialVars: Record<string, string> = {};
    vars.forEach((v) => { initialVars[v] = ""; });
    setTemplateVars(initialVars);

    // Apply immediately (with empty vars so placeholders show as-is)
    applyTemplateWithVars(template, initialVars);
  };

  const handleVarChange = (placeholder: string, value: string) => {
    if (!activeTemplate) return;
    const newVars = { ...templateVars, [placeholder]: value };
    setTemplateVars(newVars);
    applyTemplateWithVars(activeTemplate, newVars);
  };

  const allVars = Object.keys(templateVars);

  const addProcedureRow = () =>
    setProcedures((prev) => [...prev, { id: null, name: "" }]);

  const removeProcedureRow = (idx: number) =>
    setProcedures((prev) => prev.filter((_, i) => i !== idx));

  const updateProcedureRow = (idx: number, id: number | null, name: string) =>
    setProcedures((prev) =>
      prev.map((p, i) => (i === idx ? { id, name } : p))
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const contentState = editorState.getCurrentContent();
    const operationDetails = JSON.stringify(convertToRaw(contentState));
    const procedureTime = timeH ? `${timeH}:${timeM}` : "";
    onSave({
      ...formData,
      procedureTime,
      procedures: procedures.filter((p) => p.name.trim()),
      procedureName: procedures[0]?.name ?? "",
      operationDetails,
    });
  };

  const doctorOptions = doctors.map((u) => ({ value: u.id, label: userName(u) }));

  const fieldCls =
    "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary";

  return (
    <>
      <form onSubmit={handleSubmit} className="p-4 space-y-5">

        {/* ── Template picker ── */}
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-indigo-700">Use Template</span>
            <button
              type="button"
              onClick={() => setShowTemplateManager(true)}
              className="text-xs text-indigo-500 hover:text-indigo-700 hover:underline"
            >
              Manage templates
            </button>
          </div>
          <Select
            style={{ width: "100%" }}
            placeholder="Select a template to pre-fill fields…"
            value={selectedTemplateId ?? undefined}
            onChange={handleTemplateSelect}
            allowClear
            options={templates.map((t) => ({ value: t.id, label: t.name }))}
          />

          {/* Variable inputs */}
          {allVars.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="text-xs font-medium text-indigo-600 mb-1">
                Fill in the variables below — fields update live
              </div>
              <div className="grid grid-cols-2 gap-2">
                {allVars.map((placeholder) => {
                  const label = placeholder.replace(/^\{\{/, "").replace(/\}\}$/, "").replace(/_/g, " ");
                  return (
                    <div key={placeholder}>
                      <label className="block text-xs font-medium text-gray-600">{label}</label>
                      <input
                        type="text"
                        value={templateVars[placeholder]}
                        onChange={(e) => handleVarChange(placeholder, e.target.value)}
                        placeholder={placeholder}
                        className="mt-0.5 block w-full rounded border border-indigo-200 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Procedures ── */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-gray-700">
              Procedure(s) <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={addProcedureRow}
              className="text-xs font-medium text-primary hover:underline"
            >
              + Add another procedure
            </button>
          </div>

          <div className="space-y-2">
            {procedures.map((row, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <div className="flex-1">
                  <Select
                    showSearch
                    style={{ width: "100%" }}
                    placeholder="Select or search procedure…"
                    value={row.id ?? undefined}
                    onChange={(val, opt: any) => {
                      updateProcedureRow(idx, val, opt?.label ?? "");
                    }}
                    filterOption={(input, option) =>
                      String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={procedureMaster}
                    allowClear
                    onClear={() => updateProcedureRow(idx, null, "")}
                    notFoundContent={
                      <span className="text-xs text-gray-400">
                        Not listed? Add it in Masters → Procedures.
                      </span>
                    }
                  />
                </div>
                {procedures.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeProcedureRow(idx)}
                    className="text-gray-400 hover:text-red-500 text-lg leading-none px-1"
                    title="Remove"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Date + Time ── */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Date</label>
            <input
              type="date"
              value={formData.procedureDate}
              onChange={(e) => setFormData({ ...formData, procedureDate: e.target.value })}
              className={fieldCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Time</label>
            <div className="mt-1 flex items-center gap-1">
              <select
                value={timeH}
                onChange={(e) => setTimeH(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-2 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">HH</option>
                {HOUR_OPTIONS.map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              <span className="text-gray-500 font-medium">:</span>
              <select
                value={timeM}
                onChange={(e) => setTimeM(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-2 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {MINUTE_OPTIONS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ── Status ── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <Select
            style={{ width: "100%" }}
            value={formData.status}
            onChange={(v) => setFormData({ ...formData, status: v })}
            options={STATUS_OPTIONS}
          />
        </div>

        {/* ── Surgeons ── */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Surgeon 1</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select primary surgeon"
              value={formData.surgeon1 ?? undefined}
              onChange={(v) => setFormData({ ...formData, surgeon1: v ?? null })}
              allowClear
              showSearch
              filterOption={(input, opt) =>
                String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={doctorOptions}
              className="mt-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Surgeon 2</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select secondary surgeon"
              value={formData.surgeon2 ?? undefined}
              onChange={(v) => setFormData({ ...formData, surgeon2: v ?? null })}
              allowClear
              showSearch
              filterOption={(input, opt) =>
                String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={doctorOptions}
              className="mt-1"
            />
          </div>
        </div>

        {/* ── Anaesthetist + Assistant ── */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Anaesthetist</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select anaesthetist"
              value={formData.anaesthetists ?? undefined}
              onChange={(v) => setFormData({ ...formData, anaesthetists: v ?? null })}
              allowClear
              showSearch
              filterOption={(input, opt) =>
                String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={doctorOptions}
              className="mt-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Assistant</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select assistant"
              value={formData.assistants ?? undefined}
              onChange={(v) => setFormData({ ...formData, assistants: v ?? null })}
              allowClear
              showSearch
              filterOption={(input, opt) =>
                String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={doctorOptions}
              className="mt-1"
            />
          </div>
        </div>

        {/* ── Operation Details ── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Operation Details</label>
          <Editor
            editorState={editorState}
            onEditorStateChange={setEditorState}
            toolbarClassName="border border-gray-300 rounded-t-md"
            wrapperClassName="border border-gray-300 rounded-md"
            editorClassName="p-3 min-h-[160px] border-t border-gray-300"
            toolbar={{
              options: ["inline", "list", "blockType"],
              inline: { options: ["bold", "italic", "underline"] },
            }}
          />
        </div>

        {/* ── Clinical fields ── */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Findings</label>
            <textarea
              value={formData.findings}
              onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
              rows={3}
              className={fieldCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Blood Loss (ml)</label>
            <input
              type="number"
              min={0}
              value={formData.bloodLossMl}
              onChange={(e) => setFormData({ ...formData, bloodLossMl: e.target.value })}
              className={fieldCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Complications</label>
            <textarea
              value={formData.complications}
              onChange={(e) => setFormData({ ...formData, complications: e.target.value })}
              rows={2}
              className={fieldCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Implants</label>
            <textarea
              value={formData.implants}
              onChange={(e) => setFormData({ ...formData, implants: e.target.value })}
              rows={2}
              className={fieldCls}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className={fieldCls}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90"
          >
            Save OT
          </button>
        </div>
      </form>

      {/* Template manager modal */}
      <Modal
        title="OT Templates"
        open={showTemplateManager}
        onCancel={() => setShowTemplateManager(false)}
        footer={null}
        width={640}
        destroyOnClose
      >
        <TemplateManager
          onClose={() => setShowTemplateManager(false)}
          onTemplatesChanged={loadTemplates}
        />
      </Modal>
    </>
  );
};

export default OTForm;
